import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "@/App";
import { Sidebar } from "@/components/Sidebar";
import AIChat from "@/components/AIChat";
import VehicleDetailModal from "@/components/VehicleDetailModal";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Car,
  Plus,
  Search,
  Phone,
  Mail,
  MessageSquare,
  Brain,
  Sparkles,
  Target,
  Clock,
  TrendingUp,
  Users,
  ChevronRight,
  Bot,
  Zap,
  Filter,
  Link2,
  MapPin,
  DollarSign,
  Fuel,
  Gauge,
  Calendar,
  FileText,
  ArrowLeftRight,
  CheckCircle,
  AlertCircle,
  User,
  Building,
  History,
  X,
  ExternalLink,
  Eye,
} from "lucide-react";

export default function SalesPage() {
  const [leads, setLeads] = useState([]);
  const [workers, setWorkers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showChat, setShowChat] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");
  const [showAddLead, setShowAddLead] = useState(false);
  const [selectedLead, setSelectedLead] = useState(null);
  const [selectedVehicle, setSelectedVehicle] = useState(null);
  const [newLead, setNewLead] = useState({
    name: "",
    email: "",
    phone: "",
    source: "website",
    vehicle_interest: "",
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [leadsRes, workersRes] = await Promise.all([
        axios.get(`${API}/leads`),
        axios.get(`${API}/digital-workers/sales`),
      ]);
      setLeads(leadsRes.data);
      setWorkers(workersRes.data);
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddLead = async () => {
    try {
      await axios.post(`${API}/leads`, newLead);
      setShowAddLead(false);
      setNewLead({
        name: "",
        email: "",
        phone: "",
        source: "website",
        vehicle_interest: "",
      });
      fetchData();
    } catch (error) {
      console.error("Error adding lead:", error);
    }
  };

  const getScoreColor = (score) => {
    if (score >= 80) return "bg-emerald-500";
    if (score >= 60) return "bg-amber-500";
    return "bg-slate-500";
  };

  const getUrgencyBadge = (urgency) => {
    const colors = {
      high: "bg-red-500/10 text-red-500",
      medium: "bg-amber-500/10 text-amber-500",
      low: "bg-slate-500/10 text-slate-400",
    };
    return colors[urgency] || colors.low;
  };

  const filteredLeads = leads.filter((lead) => {
    const matchesSearch =
      lead.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      lead.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      lead.vehicle_interest?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesFilter =
      filterStatus === "all" || lead.status === filterStatus;
    return matchesSearch && matchesFilter;
  });

  const formatPrice = (price) => (price ? `$${price.toLocaleString()}` : "N/A");
  const formatMileage = (miles) =>
    miles !== undefined ? `${miles.toLocaleString()} mi` : "N/A";

  return (
    <div className="dashboard-layout">
      <Sidebar />
      <div className="dashboard-content">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
          <div>
            <h1
              className="font-display text-3xl font-bold text-foreground flex items-center gap-3"
              data-testid="sales-title"
            >
              <Car className="w-8 h-8 text-blue-500" />
              Sales
            </h1>
            <p className="text-muted-foreground">
              AI-powered lead management and vehicle sales
            </p>
          </div>
          <div className="flex gap-3">
            <Button
              variant="outline"
              onClick={() => setShowChat(true)}
              className="gap-2"
            >
              <Brain className="w-4 h-4" /> AI Assistant
            </Button>
            <Dialog open={showAddLead} onOpenChange={setShowAddLead}>
              <DialogTrigger asChild>
                <Button className="btn-ai gap-2" data-testid="add-lead-btn">
                  <Plus className="w-4 h-4" /> Add Lead
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Add New Lead</DialogTitle>
                </DialogHeader>
                <div className="space-y-4 py-4">
                  <Input
                    placeholder="Full Name"
                    value={newLead.name}
                    onChange={(e) =>
                      setNewLead({ ...newLead, name: e.target.value })
                    }
                  />
                  <Input
                    placeholder="Email"
                    type="email"
                    value={newLead.email}
                    onChange={(e) =>
                      setNewLead({ ...newLead, email: e.target.value })
                    }
                  />
                  <Input
                    placeholder="Phone"
                    value={newLead.phone}
                    onChange={(e) =>
                      setNewLead({ ...newLead, phone: e.target.value })
                    }
                  />
                  <Select
                    value={newLead.source}
                    onValueChange={(v) => setNewLead({ ...newLead, source: v })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Lead Source" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="website">Website</SelectItem>
                      <SelectItem value="Cars.com">Cars.com</SelectItem>
                      <SelectItem value="AutoTrader">AutoTrader</SelectItem>
                      <SelectItem value="CarGurus">CarGurus</SelectItem>
                      <SelectItem value="TrueCar">TrueCar</SelectItem>
                      <SelectItem value="walk-in">Walk-in</SelectItem>
                      <SelectItem value="phone">Phone</SelectItem>
                    </SelectContent>
                  </Select>
                  <Input
                    placeholder="Vehicle Interest"
                    value={newLead.vehicle_interest}
                    onChange={(e) =>
                      setNewLead({
                        ...newLead,
                        vehicle_interest: e.target.value,
                      })
                    }
                  />
                </div>
                <DialogFooter>
                  <Button
                    variant="outline"
                    onClick={() => setShowAddLead(false)}
                  >
                    Cancel
                  </Button>
                  <Button className="btn-ai" onClick={handleAddLead}>
                    Add Lead
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>
        </div>

        <Tabs defaultValue="leads" className="space-y-6">
          <TabsList>
            <TabsTrigger value="leads" className="gap-2">
              <Users className="w-4 h-4" /> Leads
            </TabsTrigger>
            <TabsTrigger value="workers" className="gap-2">
              <Bot className="w-4 h-4" /> Digital Workers
            </TabsTrigger>
          </TabsList>

          {/* Leads Tab */}
          <TabsContent value="leads" className="space-y-6">
            {/* Stats */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              {[
                {
                  label: "Total Leads",
                  value: leads.length,
                  icon: Users,
                  color: "text-blue-500",
                },
                {
                  label: "Hot Leads",
                  value: leads.filter((l) => l.urgency === "high").length,
                  icon: Zap,
                  color: "text-red-500",
                },
                {
                  label: "Avg Score",
                  value: leads.length
                    ? Math.round(
                        leads.reduce((a, l) => a + (l.score || 0), 0) /
                          leads.length,
                      )
                    : 0,
                  icon: Target,
                  color: "text-emerald-500",
                },
                {
                  label: "Today's Actions",
                  value: leads.filter((l) => l.next_action).length,
                  icon: Clock,
                  color: "text-amber-500",
                },
              ].map((stat, idx) => (
                <Card key={idx}>
                  <CardContent className="p-4 flex items-center gap-4">
                    <div
                      className={`w-10 h-10 rounded-lg ${stat.color.replace("text-", "bg-")}/10 flex items-center justify-center`}
                    >
                      <stat.icon className={`w-5 h-5 ${stat.color}`} />
                    </div>
                    <div>
                      <div className="text-2xl font-bold">{stat.value}</div>
                      <div className="text-xs text-muted-foreground">
                        {stat.label}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>

            {/* Search and Filter */}
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  placeholder="Search leads by name, email, or vehicle..."
                  className="pl-10"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
              <Select value={filterStatus} onValueChange={setFilterStatus}>
                <SelectTrigger className="w-40">
                  <Filter className="w-4 h-4 mr-2" />
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="new">New</SelectItem>
                  <SelectItem value="contacted">Contacted</SelectItem>
                  <SelectItem value="qualified">Qualified</SelectItem>
                  <SelectItem value="converted">Converted</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Leads List */}
            <div className="space-y-3">
              {loading ? (
                <div className="text-center py-8 text-muted-foreground">
                  Loading leads...
                </div>
              ) : filteredLeads.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  No leads found
                </div>
              ) : (
                filteredLeads.map((lead) => (
                  <Card
                    key={lead.id}
                    className="hover:border-blue-500/30 transition-all cursor-pointer"
                    data-testid={`lead-${lead.id}`}
                    onClick={() => setSelectedLead(lead)}
                  >
                    <CardContent className="p-4">
                      <div className="flex flex-col lg:flex-row lg:items-center gap-4">
                        {/* Score */}
                        <div className="flex items-center gap-3">
                          <div
                            className={`w-12 h-12 rounded-full ${getScoreColor(lead.score)} flex items-center justify-center text-white font-bold`}
                          >
                            {lead.score || 0}
                          </div>
                          <div>
                            <div className="font-semibold text-foreground">
                              {lead.name}
                            </div>
                            <div className="text-sm text-muted-foreground">
                              {lead.vehicle_interest || "No vehicle specified"}
                            </div>
                          </div>
                        </div>

                        {/* Vehicle Price if available */}
                        {lead.vehicle_details?.price && (
                          <div className="flex items-center gap-2 text-lg font-bold text-emerald-500">
                            <DollarSign className="w-4 h-4" />
                            {lead.vehicle_details.price.toLocaleString()}
                          </div>
                        )}

                        {/* Contact */}
                        <div className="flex items-center gap-4 lg:ml-auto">
                          <div className="flex items-center gap-2 text-sm text-muted-foreground">
                            <Mail className="w-4 h-4" /> {lead.email}
                          </div>
                          <div className="flex items-center gap-2 text-sm text-muted-foreground">
                            <Phone className="w-4 h-4" /> {lead.phone}
                          </div>
                        </div>

                        {/* Badges */}
                        <div className="flex items-center gap-2 flex-wrap">
                          <Badge className={getUrgencyBadge(lead.urgency)}>
                            {lead.urgency}
                          </Badge>
                          <Badge variant="outline">{lead.status}</Badge>
                          <Badge
                            variant="secondary"
                            className="flex items-center gap-1"
                          >
                            <Link2 className="w-3 h-3" />
                            {lead.source}
                          </Badge>
                        </div>

                        {/* Actions */}
                        <div className="flex items-center gap-2">
                          {lead.next_action && (
                            <Button
                              size="sm"
                              className="btn-ai gap-1"
                              onClick={(e) => e.stopPropagation()}
                            >
                              {lead.next_action === "Call" && (
                                <Phone className="w-3 h-3" />
                              )}
                              {lead.next_action === "Email" && (
                                <Mail className="w-3 h-3" />
                              )}
                              {lead.next_action === "Text" && (
                                <MessageSquare className="w-3 h-3" />
                              )}
                              {lead.next_action === "Test Drive" && (
                                <Car className="w-3 h-3" />
                              )}
                              {lead.next_action}
                            </Button>
                          )}
                          {lead.vehicle_details && (
                            <Button
                              size="sm"
                              variant="outline"
                              className="gap-1"
                              onClick={(e) => {
                                e.stopPropagation();
                                setSelectedVehicle(lead.vehicle_details);
                              }}
                              data-testid={`view-vehicle-${lead.id}`}
                            >
                              <Eye className="w-3 h-3" /> Vehicle
                            </Button>
                          )}
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedLead(lead);
                            }}
                            data-testid={`view-details-${lead.id}`}
                          >
                            View Details
                          </Button>
                        </div>
                      </div>

                      {/* AI Insights */}
                      {lead.ai_insights && (
                        <div className="mt-3 p-3 rounded-lg bg-blue-500/5 border border-blue-500/20 flex items-start gap-2">
                          <Sparkles className="w-4 h-4 text-blue-500 mt-0.5" />
                          <span className="text-sm text-blue-400">
                            {lead.ai_insights}
                          </span>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                ))
              )}
            </div>
          </TabsContent>

          {/* Digital Workers Tab */}
          <TabsContent value="workers" className="space-y-6">
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {workers.map((worker) => (
                <Card
                  key={worker.id}
                  className="hover:border-blue-500/30 transition-all group"
                  data-testid={`worker-${worker.id}`}
                >
                  <CardHeader className="pb-3">
                    <div className="flex items-start justify-between">
                      <div className="w-12 h-12 rounded-xl bg-blue-500/10 flex items-center justify-center group-hover:scale-110 transition-transform">
                        <Bot className="w-6 h-6 text-blue-500" />
                      </div>
                      <Badge className="bg-emerald-500/10 text-emerald-500">
                        {worker.status}
                      </Badge>
                    </div>
                    <CardTitle className="text-lg mt-3">
                      {worker.name}
                    </CardTitle>
                    <CardDescription className="text-blue-400">
                      {worker.subtitle}
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <p className="text-sm text-muted-foreground">
                      {worker.description}
                    </p>
                    <div className="space-y-2">
                      {worker.features?.slice(0, 3).map((feature, idx) => (
                        <div
                          key={idx}
                          className="flex items-center gap-2 text-xs text-muted-foreground"
                        >
                          <ChevronRight className="w-3 h-3 text-blue-500" />
                          {feature}
                        </div>
                      ))}
                    </div>
                    {worker.metrics && (
                      <div className="grid grid-cols-3 gap-2 pt-3 border-t border-border">
                        {Object.entries(worker.metrics)
                          .slice(0, 3)
                          .map(([key, value]) => (
                            <div key={key} className="text-center">
                              <div className="text-sm font-bold text-foreground">
                                {value}
                              </div>
                              <div className="text-xs text-muted-foreground capitalize">
                                {key.replace(/_/g, " ")}
                              </div>
                            </div>
                          ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          </TabsContent>
        </Tabs>

        {/* Lead Detail Modal */}
        <Dialog
          open={!!selectedLead}
          onOpenChange={() => setSelectedLead(null)}
        >
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden p-0">
            {selectedLead && (
              <>
                <DialogHeader className="p-6 pb-4 border-b border-border">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-4">
                      <div
                        className={`w-16 h-16 rounded-full ${getScoreColor(selectedLead.score)} flex items-center justify-center text-white text-2xl font-bold`}
                      >
                        {selectedLead.score || 0}
                      </div>
                      <div>
                        <DialogTitle className="text-2xl">
                          {selectedLead.name}
                        </DialogTitle>
                        <div className="flex items-center gap-3 mt-1">
                          <Badge
                            className={getUrgencyBadge(selectedLead.urgency)}
                          >
                            {selectedLead.urgency} priority
                          </Badge>
                          <Badge variant="outline">{selectedLead.status}</Badge>
                          <Badge variant="secondary">
                            <Link2 className="w-3 h-3 mr-1" />
                            {selectedLead.source}
                          </Badge>
                        </div>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      {selectedLead.next_action && (
                        <Button className="btn-ai gap-2">
                          {selectedLead.next_action === "Call" && (
                            <Phone className="w-4 h-4" />
                          )}
                          {selectedLead.next_action === "Email" && (
                            <Mail className="w-4 h-4" />
                          )}
                          {selectedLead.next_action}
                        </Button>
                      )}
                    </div>
                  </div>
                </DialogHeader>

                <ScrollArea className="max-h-[calc(90vh-120px)]">
                  <div className="p-6 space-y-6">
                    {/* AI Insights */}
                    {selectedLead.ai_insights && (
                      <div className="p-4 rounded-xl bg-gradient-to-r from-blue-500/10 to-purple-500/10 border border-blue-500/20">
                        <div className="flex items-center gap-2 text-blue-500 font-semibold mb-2">
                          <Sparkles className="w-5 h-5" /> AI Insights
                        </div>
                        <p className="text-blue-400">
                          {selectedLead.ai_insights}
                        </p>
                      </div>
                    )}

                    <div className="grid lg:grid-cols-2 gap-6">
                      {/* Customer Information */}
                      <Card>
                        <CardHeader className="pb-3">
                          <CardTitle className="text-lg flex items-center gap-2">
                            <User className="w-5 h-5 text-blue-500" /> Customer
                            Information
                          </CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-3">
                          <div className="flex items-center gap-3">
                            <Mail className="w-4 h-4 text-muted-foreground" />
                            <span>{selectedLead.email}</span>
                          </div>
                          <div className="flex items-center gap-3">
                            <Phone className="w-4 h-4 text-muted-foreground" />
                            <span>{selectedLead.phone}</span>
                          </div>
                          {selectedLead.customer_address && (
                            <div className="flex items-start gap-3">
                              <MapPin className="w-4 h-4 text-muted-foreground mt-0.5" />
                              <div>
                                <div>{selectedLead.customer_address}</div>
                                <div>
                                  {selectedLead.customer_city},{" "}
                                  {selectedLead.customer_state}{" "}
                                  {selectedLead.customer_zip}
                                </div>
                              </div>
                            </div>
                          )}
                        </CardContent>
                      </Card>

                      {/* Vehicle Interest */}
                      {/* {selectedLead.vehicle_details && (
                        <Card>
                          <CardHeader className="pb-3">
                            <CardTitle className="text-lg flex items-center gap-2">
                              <Car className="w-5 h-5 text-blue-500" /> Vehicle of Interest
                            </CardTitle>
                          </CardHeader>
                          <CardContent>
                            <div className="flex items-start justify-between mb-4">
                              <div>
                                <div className="text-xl font-bold text-foreground">
                                  {selectedLead.vehicle_details.year} {selectedLead.vehicle_details.make} {selectedLead.vehicle_details.model}
                                </div>
                                <div className="text-muted-foreground">{selectedLead.vehicle_details.trim}</div>
                              </div>
                              <div className="text-right">
                                <div className="text-2xl font-bold text-emerald-500">{formatPrice(selectedLead.vehicle_details.pricing.internet_price)}</div>
                                {selectedLead.vehicle_details.pricing.msrp && (
                                  <div className="text-sm text-muted-foreground line-through">MSRP {formatPrice(selectedLead.vehicle_details.pricing.msrp)}</div>
                                )}
                              </div>
                            </div>
                            <div className="grid grid-cols-2 gap-2 text-sm">
                              <div className="flex items-center gap-2"><FileText className="w-4 h-4 text-muted-foreground" /> Stock: {selectedLead.vehicle_details.identifiers.stock_number}</div>
                              <div className="flex items-center gap-2"><FileText className="w-4 h-4 text-muted-foreground" /> VIN: {selectedLead.vehicle_details.identifiers.vin?.slice(-8)}</div>
                              <div className="flex items-center gap-2"><Gauge className="w-4 h-4 text-muted-foreground" /> {formatMileage(selectedLead.vehicle_details.mileage.miles)}</div>
                              <div className="flex items-center gap-2"><Fuel className="w-4 h-4 text-muted-foreground" /> {selectedLead.vehicle_details.mileage.mpg} MPG</div>
                            </div>
                        
                            <Button 
                              className="w-full mt-4 gap-2" 
                              variant="outline"
                              onClick={() => { setSelectedLead(null); setSelectedVehicle(selectedLead.vehicle_details); }}
                              data-testid="view-vehicle-gallery-btn"
                            >
                              <Eye className="w-4 h-4" /> View Vehicle Gallery & Details
                            </Button>
                          </CardContent>
                        </Card>
                      )} */}

                      {/* Vehicle Interest */}
                      {selectedLead && (
                        <>
                          {/* FULL CARD — when vehicle_details exists */}
                          {selectedLead.vehicle_details && (
                            <Card>
                              <CardHeader className="pb-3">
                                <CardTitle className="text-lg flex items-center gap-2">
                                  <Car className="w-5 h-5 text-blue-500" />{" "}
                                  Vehicle of Interest
                                </CardTitle>
                              </CardHeader>

                              <CardContent>
                                <div className="flex items-start justify-between mb-4">
                                  <div>
                                    <div className="text-xl font-bold text-foreground">
                                      {selectedLead.vehicle_details.year}{" "}
                                      {selectedLead.vehicle_details.make}{" "}
                                      {selectedLead.vehicle_details.model}
                                    </div>

                                    {selectedLead.vehicle_details.trim && (
                                      <div className="text-muted-foreground">
                                        {selectedLead.vehicle_details.trim}
                                      </div>
                                    )}
                                  </div>

                                  <div className="text-right">
                                    <div className="text-2xl font-bold text-emerald-500">
                                      {formatPrice(
                                        selectedLead.vehicle_details.pricing
                                          .internet_price,
                                      )}
                                    </div>

                                    {selectedLead.vehicle_details.pricing
                                      .msrp && (
                                      <div className="text-sm text-muted-foreground line-through">
                                        MSRP{" "}
                                        {formatPrice(
                                          selectedLead.vehicle_details.pricing
                                            .msrp,
                                        )}
                                      </div>
                                    )}
                                  </div>
                                </div>

                                <div className="grid grid-cols-2 gap-2 text-sm">
                                  <div className="flex items-center gap-2">
                                    <FileText className="w-4 h-4 text-muted-foreground" />
                                    Stock:{" "}
                                    {
                                      selectedLead.vehicle_details.identifiers
                                        .stock_number
                                    }
                                  </div>

                                  <div className="flex items-center gap-2">
                                    <FileText className="w-4 h-4 text-muted-foreground" />
                                    VIN:{" "}
                                    {selectedLead.vehicle_details.identifiers.vin?.slice(
                                      -8,
                                    )}
                                  </div>

                                  <div className="flex items-center gap-2">
                                    <Gauge className="w-4 h-4 text-muted-foreground" />
                                    {formatMileage(
                                      selectedLead.vehicle_details.mileage
                                        .miles,
                                    )}
                                  </div>

                                  <div className="flex items-center gap-2">
                                    <Fuel className="w-4 h-4 text-muted-foreground" />
                                    {selectedLead.vehicle_details.mileage.mpg}{" "}
                                    MPG
                                  </div>
                                </div>

                                <Button
                                  className="w-full mt-4 gap-2"
                                  variant="outline"
                                  onClick={() => {
                                    setSelectedLead(null);
                                    setSelectedVehicle(
                                      selectedLead.vehicle_details,
                                    );
                                  }}
                                >
                                  <Eye className="w-4 h-4" /> View Vehicle
                                  Gallery & Details
                                </Button>
                              </CardContent>
                            </Card>
                          )}

                          {/* MINIMAL CARD — when ONLY vehicle_interest exists */}
                          {!selectedLead.vehicle_details &&
                            selectedLead.vehicle_interest && (
                              <Card>
                                <CardHeader className="pb-3">
                                  <CardTitle className="text-lg flex items-center gap-2">
                                    <Car className="w-5 h-5 text-blue-500" />{" "}
                                    Vehicle of Interest
                                  </CardTitle>
                                </CardHeader>

                                <CardContent>
                                  <div className="text-xl font-bold text-foreground">
                                    {selectedLead.vehicle_interest}
                                  </div>
                                </CardContent>
                              </Card>
                            )}
                        </>
                      )}
                    </div>

                    {/* Vehicle Specifications */}
                    {selectedLead.vehicle_details && (
                      <Card>
                        <CardHeader className="pb-3">
                          <CardTitle className="text-lg">
                            Vehicle Specifications
                          </CardTitle>
                        </CardHeader>
                        <CardContent>
                          <div className="grid md:grid-cols-3 gap-6">
                            <div className="space-y-3">
                              <h4 className="font-semibold text-sm text-muted-foreground uppercase tracking-wider">
                                Exterior
                              </h4>
                              <div className="space-y-2 text-sm">
                                <div className="flex justify-between">
                                  <span className="text-muted-foreground">
                                    Color
                                  </span>
                                  <span>
                                    {
                                      selectedLead.vehicle_details.exterior
                                        .color
                                    }
                                  </span>
                                </div>
                                <div className="flex justify-between">
                                  <span className="text-muted-foreground">
                                    Body Style
                                  </span>
                                  <span>
                                    {
                                      selectedLead.vehicle_details.exterior
                                        .body_style
                                    }
                                  </span>
                                </div>
                                <div className="flex justify-between">
                                  <span className="text-muted-foreground">
                                    Doors
                                  </span>
                                  <span>
                                    {
                                      selectedLead.vehicle_details.exterior
                                        .doors
                                    }
                                  </span>
                                </div>
                              </div>
                            </div>
                            <div className="space-y-3">
                              <h4 className="font-semibold text-sm text-muted-foreground uppercase tracking-wider">
                                Interior
                              </h4>
                              <div className="space-y-2 text-sm">
                                <div className="flex justify-between">
                                  <span className="text-muted-foreground">
                                    Color
                                  </span>
                                  <span>
                                    {
                                      selectedLead.vehicle_details.interior
                                        .color
                                    }
                                  </span>
                                </div>
                                <div className="flex justify-between">
                                  <span className="text-muted-foreground">
                                    Condition
                                  </span>
                                  <span>
                                    {
                                      selectedLead.vehicle_details.condition
                                        .status
                                    }
                                  </span>
                                </div>
                              </div>
                            </div>
                            <div className="space-y-3">
                              <h4 className="font-semibold text-sm text-muted-foreground uppercase tracking-wider">
                                Performance
                              </h4>
                              <div className="space-y-2 text-sm">
                                <div className="flex justify-between">
                                  <span className="text-muted-foreground">
                                    Engine
                                  </span>
                                  <span>
                                    {
                                      selectedLead.vehicle_details.performance
                                        .engine
                                    }
                                  </span>
                                </div>
                                <div className="flex justify-between">
                                  <span className="text-muted-foreground">
                                    Transmission
                                  </span>
                                  <span>
                                    {
                                      selectedLead.vehicle_details.performance
                                        .transmission
                                    }
                                  </span>
                                </div>
                                <div className="flex justify-between">
                                  <span className="text-muted-foreground">
                                    Drivetrain
                                  </span>
                                  <span>
                                    {
                                      selectedLead.vehicle_details.performance
                                        .drivetrain
                                    }
                                  </span>
                                </div>
                                <div className="flex justify-between">
                                  <span className="text-muted-foreground">
                                    Fuel Type
                                  </span>
                                  <span>
                                    {
                                      selectedLead.vehicle_details.performance
                                        .fuel_type
                                    }
                                  </span>
                                </div>
                              </div>
                            </div>
                          </div>

                          {/* Features */}
                          {selectedLead.vehicle_details.features?.length >
                            0 && (
                            <div className="mt-6">
                              <h4 className="font-semibold text-sm text-muted-foreground uppercase tracking-wider mb-3">
                                Features
                              </h4>
                              <div className="flex flex-wrap gap-2">
                                {selectedLead.vehicle_details.features.map(
                                  (feature, idx) => (
                                    <Badge
                                      key={idx}
                                      variant="outline"
                                      className="text-xs"
                                    >
                                      <CheckCircle className="w-3 h-3 mr-1 text-emerald-500" />
                                      {feature}
                                    </Badge>
                                  ),
                                )}
                              </div>
                            </div>
                          )}

                          {/* Packages */}
                          {selectedLead.vehicle_details.packages?.length >
                            0 && (
                            <div className="mt-4">
                              <h4 className="font-semibold text-sm text-muted-foreground uppercase tracking-wider mb-3">
                                Packages Included
                              </h4>
                              <div className="flex flex-wrap gap-2">
                                {selectedLead.vehicle_details.packages.map(
                                  (pkg, idx) => (
                                    <Badge
                                      key={idx}
                                      className="bg-blue-500/10 text-blue-500"
                                    >
                                      {pkg}
                                    </Badge>
                                  ),
                                )}
                              </div>
                            </div>
                          )}
                        </CardContent>
                      </Card>
                    )}

                    {/* Trade-In Information */}
                    {selectedLead.trade_in && (
                      <Card className="border-amber-500/30">
                        <CardHeader className="pb-3">
                          <CardTitle className="text-lg flex items-center gap-2">
                            <ArrowLeftRight className="w-5 h-5 text-amber-500" />{" "}
                            Trade-In Vehicle
                          </CardTitle>
                        </CardHeader>
                        <CardContent>
                          <div className="flex items-start justify-between">
                            <div>
                              <div className="text-lg font-bold text-foreground">
                                {selectedLead.trade_in.year}{" "}
                                {selectedLead.trade_in.make}{" "}
                                {selectedLead.trade_in.model}{" "}
                                {selectedLead.trade_in.trim}
                              </div>
                              <div className="text-sm text-muted-foreground mt-1">
                                {formatMileage(selectedLead.trade_in.mileage)} •{" "}
                                {selectedLead.trade_in.condition} Condition
                              </div>
                              {selectedLead.trade_in.vin && (
                                <div className="text-xs text-muted-foreground mt-1">
                                  VIN: {selectedLead.trade_in.vin}
                                </div>
                              )}
                            </div>
                            <div className="text-right">
                              <div className="text-xl font-bold text-amber-500">
                                {formatPrice(
                                  selectedLead.trade_in.estimated_value,
                                )}
                              </div>
                              <div className="text-sm text-muted-foreground">
                                Estimated Value
                              </div>
                              {selectedLead.trade_in.payoff > 0 && (
                                <div className="text-sm text-red-400 mt-1">
                                  Payoff:{" "}
                                  {formatPrice(selectedLead.trade_in.payoff)}
                                </div>
                              )}
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    )}

                    {/* Communication History */}
                    {selectedLead.communication_history?.length > 0 && (
                      <Card>
                        <CardHeader className="pb-3">
                          <CardTitle className="text-lg flex items-center gap-2">
                            <History className="w-5 h-5 text-blue-500" />{" "}
                            Communication History
                          </CardTitle>
                        </CardHeader>
                        <CardContent>
                          <div className="space-y-4">
                            {selectedLead.communication_history.map(
                              (comm, idx) => (
                                <div
                                  key={idx}
                                  className="flex items-start gap-4 pb-4 border-b border-border last:border-0 last:pb-0"
                                >
                                  <div
                                    className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${
                                      comm.type.includes("phone")
                                        ? "bg-emerald-500/10"
                                        : comm.type.includes("email")
                                          ? "bg-blue-500/10"
                                          : comm.type.includes("sms") ||
                                              comm.type.includes("text")
                                            ? "bg-purple-500/10"
                                            : "bg-slate-500/10"
                                    }`}
                                  >
                                    {comm.type.includes("phone") && (
                                      <Phone className="w-5 h-5 text-emerald-500" />
                                    )}
                                    {comm.type.includes("email") && (
                                      <Mail className="w-5 h-5 text-blue-500" />
                                    )}
                                    {(comm.type.includes("sms") ||
                                      comm.type.includes("text")) && (
                                      <MessageSquare className="w-5 h-5 text-purple-500" />
                                    )}
                                    {comm.type.includes("walk") && (
                                      <Building className="w-5 h-5 text-slate-400" />
                                    )}
                                    {comm.type.includes("lead") && (
                                      <ExternalLink className="w-5 h-5 text-slate-400" />
                                    )}
                                    {comm.type.includes("credit") && (
                                      <FileText className="w-5 h-5 text-amber-500" />
                                    )}
                                    {comm.type.includes("appraisal") && (
                                      <Car className="w-5 h-5 text-amber-500" />
                                    )}
                                    {comm.type.includes("service") && (
                                      <Car className="w-5 h-5 text-cyan-500" />
                                    )}
                                    {comm.type.includes("handoff") && (
                                      <ArrowLeftRight className="w-5 h-5 text-purple-500" />
                                    )}
                                  </div>
                                  <div className="flex-1">
                                    <div className="flex items-center justify-between">
                                      <span className="font-medium text-foreground capitalize">
                                        {comm.type.replace(/_/g, " ")}
                                      </span>
                                      <span className="text-xs text-muted-foreground">
                                        {new Date(
                                          comm.timestamp,
                                        ).toLocaleDateString()}{" "}
                                        {new Date(
                                          comm.timestamp,
                                        ).toLocaleTimeString([], {
                                          hour: "2-digit",
                                          minute: "2-digit",
                                        })}
                                      </span>
                                    </div>
                                    <p className="text-sm text-muted-foreground mt-1">
                                      {comm.message}
                                    </p>
                                  </div>
                                </div>
                              ),
                            )}
                          </div>
                        </CardContent>
                      </Card>
                    )}
                  </div>
                </ScrollArea>
              </>
            )}
          </DialogContent>
        </Dialog>
      </div>

      <AIChat
        isOpen={showChat}
        onClose={() => setShowChat(false)}
        vertical="sales"
      />

      {/* Carvana-style Vehicle Detail Modal */}
      <VehicleDetailModal
        vehicle={selectedVehicle}
        open={!!selectedVehicle}
        onClose={() => setSelectedVehicle(null)}
      />

      {!showChat && (
        <button
          onClick={() => setShowChat(true)}
          className="fixed bottom-6 right-6 w-14 h-14 rounded-full btn-ai flex items-center justify-center shadow-lg hover:scale-110 transition-transform"
          style={{ zIndex: 9998 }}
          data-testid="chat-fab"
        >
          <MessageSquare className="w-6 h-6" />
        </button>
      )}
    </div>
  );
}
